import { Controller } from "@hotwired/stimulus"
import { fireflyAnchors, FIREFLY_COUNT, gathersForKith, nightEnough } from "../lair/kith_fireflies.js"
import { shouldArmFallingStars, starPlacement, starWaitMs } from "../lair/falling_star.js"
import {
  parseViewBox, formatViewBox, clampViewBox, panViewBox, zoomAtPoint, slackFor, wheelZoomFactor
} from "../lair/camera.js"

// How far in the map may be read. The valley is 960 across; a third of that is
// close enough to pick out who is standing at a fire.
const MAP_MIN_SPAN = 0.34
const ARROW_STEP = 0.14
// Once gathered, the fireflies only blink; a blink read a dozen times a second
// looks as a blink read sixty times does, and costs the map a fifth of the
// repaints. The gather itself still moves every frame.
const FIREFLY_REST_MS = 80

// The drawn grounds map: after dark, fireflies gather where your kith are
// standing. Nobody announces it — you notice the meadow leaning toward a fire.
export default class extends Controller {
  static targets = [ "fireflies" ]
  static values = {
    phase: String,
    stillnight: Boolean,
    kithVenues: Array,
    venueSpots: Object
  }

  connect() {
    this.reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches
    this.anchors = fireflyAnchors(this.kithVenuesValue, this.venueSpotsValue)
    this.positions = this.anchors.map((anchor) => ({ x: anchor.x, y: anchor.y }))
    this.blinkPhase = 0

    this.drawFireflies()
    this.initCamera()

    if (this.shouldGather()) {
      if (this.reducedMotion) {
        this.positions = this.anchors.map((anchor) => ({ ...anchor.home }))
        this.drawFireflies()
      } else {
        this.gatherStart = performance.now()
        this.gatherDuration = 22000
        this.loop = this.loop.bind(this)
        this.frame = requestAnimationFrame(this.loop)
      }
    }

    // The sky falls here too on the stillnight — same streak as the lair, but
    // silent. No wish on the map; that belongs to the hearth.
    if (shouldArmFallingStars({ phase: this.phaseValue, reducedMotion: this.reducedMotion, editable: true })) {
      this.armStar()
    }

    // The place tapped lights at once (ll-vale-place--going) and stays lit
    // until its page is drawn over this one. Turbo says turbo:click on the
    // link it is about to follow; a tap the pan logic swallowed never gets
    // there, so a dragged-over place does not light.
    this.onGoing = this.onGoing.bind(this)
    this.onStayed = this.onStayed.bind(this)
    this.element.addEventListener("turbo:click", this.onGoing)
    document.addEventListener("turbo:fetch-request-error", this.onStayed)
  }

  disconnect() {
    if (this.frame) cancelAnimationFrame(this.frame)
    clearTimeout(this.restTimer)
    if (this.starTimer) clearTimeout(this.starTimer)
    this.unbindCamera()
    this.element.removeEventListener("turbo:click", this.onGoing)
    document.removeEventListener("turbo:fetch-request-error", this.onStayed)
  }

  onGoing(event) {
    const place = event.target.closest?.(".ll-vale-place")
    if (!place) return

    this.onStayed()
    place.classList.add("ll-vale-place--going")
    place.setAttribute("aria-busy", "true")
  }

  // The page did not come (offline, a dropped connection): the place goes
  // back to how it was, so the map does not sit lit for a walk not taken.
  onStayed() {
    this.element.querySelectorAll(".ll-vale-place--going").forEach((place) => {
      place.classList.remove("ll-vale-place--going")
      place.removeAttribute("aria-busy")
    })
  }

  // The map had no camera at all: one fixed drawing scaled to fit its frame, so
  // there was nothing for a wheel or a held drag to act on. It is the same lens
  // the lair uses — the SVG viewBox — driven by the same primitives, so zoom,
  // pan, clamping and the drag/click distinction all behave the way they do
  // inside a place.
  initCamera() {
    this.svg = this.element.querySelector("svg.ll-grounds-map")
    if (!this.svg) return

    this.home = parseViewBox(this.svg.getAttribute("viewBox"))
    this.sceneBounds = { width: this.home.width, height: this.home.height }
    this.camera = this.openingFrame()
    this.limits = {
      minWidth: this.home.width * MAP_MIN_SPAN,
      minHeight: this.home.height * MAP_MIN_SPAN
    }
    this.panCandidate = null
    this.panning = false
    this.didPan = false
    this.touches = new Map()
    this.pinch = null
    this.applyCamera(this.camera)

    this.onWheel = this.onWheel.bind(this)
    this.onPanStart = this.onPanStart.bind(this)
    this.onPanMove = this.onPanMove.bind(this)
    this.onPanEnd = this.onPanEnd.bind(this)
    this.onClickCapture = this.onClickCapture.bind(this)
    this.onKeyDown = this.onKeyDown.bind(this)
    this.onTouchDown = this.onTouchDown.bind(this)
    this.onTouchMove = this.onTouchMove.bind(this)
    this.onTouchUp = this.onTouchUp.bind(this)

    this.svg.addEventListener("wheel", this.onWheel, { passive: false })
    this.svg.addEventListener("mousedown", this.onPanStart)
    this.svg.addEventListener("click", this.onClickCapture, true)
    this.svg.addEventListener("keydown", this.onKeyDown)
    window.addEventListener("mousemove", this.onPanMove)
    window.addEventListener("mouseup", this.onPanEnd)
    // Fingers, as in the lair: one pans, two pinch. Mouse events never arrive
    // for a moving touch, so without these the map's "pinch to zoom" was a
    // promise the phone could not keep — touch-action: none had already taken
    // the browser's own pinch away.
    this.svg.addEventListener("pointerdown", this.onTouchDown)
    window.addEventListener("pointermove", this.onTouchMove)
    window.addEventListener("pointerup", this.onTouchUp)
    window.addEventListener("pointercancel", this.onTouchUp)
  }

  // The drawn map is wide — 1344 by 644 — and the whole of it is the opening
  // frame on a wide screen. In a portrait frame that same box letterboxes to a
  // strip a finger's width tall, with 19px destinations. So when the frame is
  // taller than the drawing, open on a box of the frame's own aspect, as tall
  // as the map and centred on it: the valley fills the frame at roughly one
  // and a half times the size, and a pan reaches the coasts.
  openingFrame() {
    const rect = this.svg.getBoundingClientRect()
    const homeAspect = this.home.width / this.home.height
    if (!rect.width || !rect.height || rect.width / rect.height >= homeAspect - 0.01) return { ...this.home }

    const width = this.home.height * (rect.width / rect.height)
    return clampViewBox({
      x: this.home.x + (this.home.width - width) / 2,
      y: this.home.y,
      width,
      height: this.home.height
    }, this.sceneBounds)
  }

  unbindCamera() {
    if (!this.svg) return

    this.svg.removeEventListener("wheel", this.onWheel)
    this.svg.removeEventListener("mousedown", this.onPanStart)
    this.svg.removeEventListener("click", this.onClickCapture, true)
    this.svg.removeEventListener("keydown", this.onKeyDown)
    window.removeEventListener("mousemove", this.onPanMove)
    window.removeEventListener("mouseup", this.onPanEnd)
    this.svg.removeEventListener("pointerdown", this.onTouchDown)
    window.removeEventListener("pointermove", this.onTouchMove)
    window.removeEventListener("pointerup", this.onTouchUp)
    window.removeEventListener("pointercancel", this.onTouchUp)
  }

  onTouchDown(event) {
    if (event.pointerType !== "touch") return

    this.touches.set(event.pointerId, { x: event.clientX, y: event.clientY })

    if (this.touches.size === 1) {
      this.pinch = null
      this.didPan = false
      this.panCandidate = { x: event.clientX, y: event.clientY, camera: { ...this.camera } }
      return
    }

    if (this.touches.size === 2) {
      this.panCandidate = null
      this.panning = false
      const [ a, b ] = [ ...this.touches.values() ]
      const center = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }
      this.pinch = {
        camera: { ...this.camera },
        distance: Math.max(1, Math.hypot(a.x - b.x, a.y - b.y)),
        center,
        anchor: this.scenePoint({ clientX: center.x, clientY: center.y })
      }
      this.didPan = true
    }
  }

  onTouchMove(event) {
    if (event.pointerType !== "touch" || !this.touches.has(event.pointerId)) return

    this.touches.set(event.pointerId, { x: event.clientX, y: event.clientY })

    if (this.pinch && this.touches.size >= 2) {
      const [ a, b ] = [ ...this.touches.values() ]
      const distance = Math.hypot(a.x - b.x, a.y - b.y)
      if (distance < 1 || !this.pinch.anchor) return

      let next = zoomAtPoint(this.pinch.camera, this.pinch.anchor, distance / this.pinch.distance, this.sceneBounds, this.limits)
      const center = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }
      const unitsPerPixel = this.unitsPerPixel(next)
      next = panViewBox(
        next,
        (this.pinch.center.x - center.x) * unitsPerPixel,
        (this.pinch.center.y - center.y) * unitsPerPixel,
        this.sceneBounds
      )
      this.applyCamera(next)
      event.preventDefault()
      return
    }

    if (this.panCandidate) this.onPanMove(event)
  }

  onTouchUp(event) {
    if (event.pointerType !== "touch" || !this.touches.has(event.pointerId)) return

    this.touches.delete(event.pointerId)
    if (this.touches.size < 2) this.pinch = null
    if (this.touches.size === 0) this.onPanEnd()
  }

  // Map units per CSS pixel. The svg keeps its aspect, so the tighter axis
  // sets the scale for both.
  unitsPerPixel(box = this.camera) {
    const rect = this.svg.getBoundingClientRect()
    if (!rect.width || !rect.height) return 1

    return Math.max(box.width / rect.width, box.height / rect.height)
  }

  applyCamera(box) {
    this.camera = box
    this.svg.setAttribute("viewBox", formatViewBox(box))
    this.svg.classList.toggle("ll-grounds-map--zoomed", box.width < this.home.width - 2)
  }

  onWheel(event) {
    event.preventDefault()
    const point = this.scenePoint(event)
    if (!point) return

    this.applyCamera(zoomAtPoint(
      this.camera, point, wheelZoomFactor(event), this.sceneBounds, this.limits
    ))
  }

  // Where the pointer is in the map's own coordinates, which is what a zoom has
  // to hold still under the cursor.
  scenePoint(event) {
    const rect = this.svg.getBoundingClientRect()
    if (!rect.width || !rect.height) return null

    return {
      x: this.camera.x + ((event.clientX - rect.left) / rect.width) * this.camera.width,
      y: this.camera.y + ((event.clientY - rect.top) / rect.height) * this.camera.height
    }
  }

  onPanStart(event) {
    if (event.button !== 0) return

    // A fresh press clears the last gesture's verdict, or a drag that ended
    // without a click leaves the flag armed and swallows the next real click on
    // a destination.
    this.didPan = false
    this.panCandidate = { x: event.clientX, y: event.clientY, camera: { ...this.camera } }
  }

  onPanMove(event) {
    if (!this.panCandidate) return

    if (!this.panning) {
      const drift = Math.hypot(
        event.clientX - this.panCandidate.x, event.clientY - this.panCandidate.y
      )
      // Under the threshold it is still a click, so every destination on the map
      // stays clickable; past it the hand has the camera.
      if (drift < 5) return

      this.panning = true
      this.didPan = true
      this.panStart = this.panCandidate
      this.svg.style.cursor = "grabbing"
    }

    const unitsPerPixel = this.unitsPerPixel(this.panStart.camera)
    const dx = (this.panStart.x - event.clientX) * unitsPerPixel
    const dy = (this.panStart.y - event.clientY) * unitsPerPixel

    this.applyCamera(panViewBox(
      this.panStart.camera, dx, dy, this.sceneBounds, slackFor(this.panStart.camera)
    ))
    event.preventDefault()
  }

  onPanEnd() {
    if (this.panning) {
      const settled = clampViewBox(this.camera, this.sceneBounds)
      if (settled.x !== this.camera.x || settled.y !== this.camera.y) this.applyCamera(settled)
    }

    this.panning = false
    this.panStart = null
    this.panCandidate = null
    if (this.svg) this.svg.style.cursor = ""
  }

  // A drag that ends over a destination must not also travel there.
  onClickCapture(event) {
    if (!this.didPan) return

    this.didPan = false
    event.preventDefault()
    event.stopPropagation()
  }

  onKeyDown(event) {
    const step = { ArrowLeft: [ -1, 0 ], ArrowRight: [ 1, 0 ], ArrowUp: [ 0, -1 ], ArrowDown: [ 0, 1 ] }[event.key]
    if (!step) return

    event.preventDefault()
    this.applyCamera(panViewBox(
      this.camera,
      step[0] * this.camera.width * ARROW_STEP,
      step[1] * this.camera.height * ARROW_STEP,
      this.sceneBounds
    ))
  }

  shouldGather() {
    return nightEnough(this.phaseValue) && gathersForKith(this.kithVenuesValue)
  }

  drawFireflies() {
    const ns = "http://www.w3.org/2000/svg"
    const group = this.firefliesTarget
    group.replaceChildren()

    for (let i = 0; i < FIREFLY_COUNT; i++) {
      const pos = this.positions[i]
      const glow = document.createElementNS(ns, "circle")
      glow.setAttribute("class", "ll-grounds-firefly")
      glow.setAttribute("cx", pos.x.toFixed(1))
      glow.setAttribute("cy", pos.y.toFixed(1))
      glow.setAttribute("r", (1.1 + (i % 3) * 0.15).toFixed(2))
      glow.dataset.index = String(i)
      group.appendChild(glow)
    }
  }

  loop(now) {
    if (!this.shouldGather()) return

    const t = Math.min(1, (now - this.gatherStart) / this.gatherDuration)
    const ease = t * t * (3 - 2 * t)

    this.anchors.forEach((anchor, i) => {
      this.positions[i] = {
        x: anchor.x + ((anchor.home.x - anchor.x) * ease),
        y: anchor.y + ((anchor.home.y - anchor.y) * ease)
      }
    })

    this.blinkPhase = now
    const stillnightDim = this.stillnightValue ? 0.55 : 1
    const gathered = t >= 1
    this.firefliesTarget.querySelectorAll(".ll-grounds-firefly").forEach((node, i) => {
      if (!gathered) {
        const pos = this.positions[i]
        node.setAttribute("cx", pos.x.toFixed(1))
        node.setAttribute("cy", pos.y.toFixed(1))
      }
      const blink = (0.35 + (0.25 * Math.sin((now / 1000) * 2.4 + i * 1.7))) * stillnightDim
      node.setAttribute("opacity", blink.toFixed(2))
    })

    if (gathered) {
      this.restTimer = setTimeout(() => { this.frame = requestAnimationFrame(this.loop) }, FIREFLY_REST_MS)
    } else {
      this.frame = requestAnimationFrame(this.loop)
    }
  }

  armStar() {
    const wait = starWaitMs({ stillnight: this.stillnightValue })
    this.starTimer = setTimeout(() => this.fallStar(), wait)
  }

  fallStar() {
    const star = document.createElement("span")
    star.className = "ll-star ll-star--map"
    star.setAttribute("aria-hidden", "true")
    const place = starPlacement()
    star.style.setProperty("--star-x", place.x)
    star.style.setProperty("--star-y", place.y)
    this.element.appendChild(star)
    star.addEventListener("animationend", () => star.remove())

    this.armStar()
  }
}
